import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';

const navLinks = [
  { to: '/', label: '首页' },
  ...categories.map((c) => ({ to: `/category/${c.slug}`, label: c.name })),
  { to: '/about', label: '关于我们' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [focus, setFocus] = useState(false);
  const { pathname } = useLocation();

  const results = query.trim()
    ? comics
        .filter(
          (c) =>
            c.title.includes(query.trim()) ||
            c.tags.some((t) => t.includes(query.trim())),
        )
        .slice(0, 5)
    : [];

  const isActive = (to: string) => {
    if (to === '/') return pathname === '/';
    return pathname.startsWith(to);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-background-200/60 bg-background-50/90 backdrop-blur-md">
      <nav className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-4 px-4 md:px-6" aria-label="主导航">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-skull-line text-xl" aria-hidden="true"></i>
          </span>
          <span className="font-heading text-xl tracking-wide text-foreground-950">
            嘿嘿<span className="text-primary-500">漫画网</span>
          </span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                isActive(link.to)
                  ? 'bg-secondary-100 text-foreground-950'
                  : 'text-foreground-600 hover:bg-secondary-100/60 hover:text-foreground-950'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="relative hidden items-center md:flex">
          <i className="ri-search-line pointer-events-none absolute left-3 text-sm text-foreground-500" aria-hidden="true"></i>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocus(true)}
            onBlur={() => setTimeout(() => setFocus(false), 150)}
            placeholder="搜索漫画 / 题材"
            className="w-52 rounded-full border border-background-300/60 bg-background-100 py-2 pl-9 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
          />
          {focus && results.length > 0 && (
            <div className="absolute top-full right-0 mt-2 w-72 overflow-hidden rounded-xl border border-background-200 bg-background-100 shadow-2xl">
              {results.map((c) => (
                <Link
                  key={c.id}
                  to={`/comic/${c.id}`}
                  className="flex items-center gap-3 px-3 py-2 transition-colors hover:bg-secondary-100"
                >
                  <span className="h-10 w-8 shrink-0 overflow-hidden rounded bg-background-300">
                    <img src={c.cover} alt={c.title} className="h-full w-full object-cover object-top" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-foreground-950">{c.title}</span>
                    <span className="block text-xs text-foreground-500">{c.tags[0]}</span>
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-950 md:hidden"
          aria-label="打开菜单"
        >
          <i className={open ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} aria-hidden="true"></i>
        </button>
      </nav>

      {open && (
        <div className="border-t border-background-200/60 bg-background-100 px-4 py-3 md:hidden">
          <div className="mb-3 flex items-center rounded-full border border-background-300/60 bg-background-50 px-3 py-2">
            <i className="ri-search-line mr-2 text-sm text-foreground-500" aria-hidden="true"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索漫画 / 题材"
              className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className={`rounded-md px-3 py-2.5 text-sm ${
                  isActive(link.to)
                    ? 'bg-secondary-100 text-foreground-950'
                    : 'text-foreground-600 hover:bg-secondary-100/60'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
          {query.trim() && results.length > 0 && (
            <div className="mt-2 border-t border-background-200/60 pt-2">
              {results.map((c) => (
                <Link
                  key={c.id}
                  to={`/comic/${c.id}`}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-2 py-2"
                >
                  <span className="h-10 w-8 shrink-0 overflow-hidden rounded bg-background-300">
                    <img src={c.cover} alt={c.title} className="h-full w-full object-cover object-top" />
                  </span>
                  <span className="text-sm text-foreground-950">{c.title}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </header>
  );
}